import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Dispatch, SetStateAction } from "react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedCard } from "@/components/layout/locked-content";
import { FeedCard, type FeedCardProps } from "@/components/ai-consumer-research/live-feed/feed-card";

import { message } from "@/lib/utils";
import { normalizeSentiment } from "@/lib/chart";
import { ConsumerResearchApiFactory } from "@/apis/brand-os/consumer-research";
import type { FeedComment, FeedData } from "@/schema/brand-os/ai-consumer-research";

import type { ChannelValue, SentimentValue } from "@/components/ai-consumer-research/live-feed/filters";

interface FeedProps {
  sentimentFilter: SentimentValue;
  channelFilter: ChannelValue;
}

interface FeedContentProps {
  data?: FeedData;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  sentimentFilter: SentimentValue;
  channelFilter: ChannelValue;
}

function CardItem(comment: FeedComment): FeedCardProps {
  const tags = comment.tags?.length ? comment.tags : ([comment.brand, comment.channel].filter(Boolean) as string[]);
  const sentiment = normalizeSentiment(comment.sentiment);

  const metadataEngagement = Object.entries((comment.metadata as Record<string, unknown>) || {}).reduce((sum, [, value]) => {
    const numeric = typeof value === "number" ? value : Number(value);
    return sum + (Number.isFinite(numeric) ? numeric : 0);
  }, 0);

  const engagements = metadataEngagement > 0 ? String(metadataEngagement) : String(comment.engagements ?? comment.likes ?? 0);

  return {
    source: comment.user_name || comment.channel || "Unknown Source",
    profileLink: comment.link || comment.post_url || undefined,
    content: comment.content || comment.comment || comment.text || "",
    platform: comment.platform || comment.platfrom || comment.channel || undefined,
    tags,
    sentiment,
    engagements,
  };
}

function FeedContent({ data, page, setPage, sentimentFilter, channelFilter }: FeedContentProps) {
  const items = useMemo(
    () =>
      (data?.comments ?? []).map(CardItem).filter((item) => {
        const sentimentPass = sentimentFilter === "All" || item.sentiment.toLowerCase() === sentimentFilter.toLowerCase();
        const channelPass = channelFilter === "All" || item.platform?.toLowerCase() === channelFilter.toLowerCase() || item.tags.some((tag) => tag.toLowerCase() === channelFilter.toLowerCase());
        return sentimentPass && channelPass;
      }),
    [data?.comments, sentimentFilter, channelFilter]
  );

  const { page: currentPage = page, total_pages: totalPages = currentPage, has_previous: hasPrevious = currentPage > 1, has_next: hasNext = currentPage < totalPages } = data?.pagination ?? {};

  return (
    <div className="space-y-4">
      {items.length ? items.map((item, index) => <FeedCard key={`${item.source}-${index}`} {...item} />) : <LockedCard message={message} className="min-h-[60vh]" />}

      {items.length > 0 ? (
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" disabled={!hasPrevious} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {currentPage}
            {totalPages ? ` of ${totalPages}` : ""}
          </span>
          <Button variant="outline" size="sm" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function FeedSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 4 }, (_, i) => (
        <Skeleton key={i} className="h-32 w-full rounded-xl" />
      ))}
    </div>
  );
}

export function Feed({ sentimentFilter, channelFilter }: FeedProps) {
  const [page, setPage] = useState(1);
  const { data, status } = useQuery(ConsumerResearchApiFactory.Queries.Feed({ page }));

  switch (status) {
    case "pending":
      return <FeedSkeleton />;
    case "error":
      return <LockedCard message={message} className="min-h-[60vh]" />;
    case "success":
      return <FeedContent data={data} page={page} setPage={setPage} sentimentFilter={sentimentFilter} channelFilter={channelFilter} />;
  }
}
